import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Snackbar,
  Tooltip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import UploadIcon from '@mui/icons-material/Upload';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../hooks/reduxHooks';
import doctorService, {
  Doctor,
  DoctorTemplateBinding,
  BulkImportResult,
} from '../services/doctorService';
import PageHeader from '../components/PageHeader';
import RichReportEditor from '../components/RichReportEditor';

const DoctorManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const canGoBackInApp = Boolean(location.key && location.key !== 'default');
  const { user: currentUser } = useAppSelector((state) => state.auth);
  const isAdmin = currentUser?.role === 'admin';

  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [doctorsTotal, setDoctorsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Create/Edit doctor dialog
  const [doctorDialogOpen, setDoctorDialogOpen] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState<Doctor | null>(null);
  const [doctorForm, setDoctorForm] = useState({ name: '', npi: '', specialty: '' });
  const [doctorError, setDoctorError] = useState('');
  const [doctorSaving, setDoctorSaving] = useState(false);

  // Delete doctor
  const [deleteTarget, setDeleteTarget] = useState<Doctor | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Bulk import
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [bulkCsvText, setBulkCsvText] = useState('');
  const [bulkImportLoading, setBulkImportLoading] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState<BulkImportResult | null>(null);

  // Bindings state per-doctor (expanded accordion)
  const [bindingsByDoctor, setBindingsByDoctor] = useState<Record<number, DoctorTemplateBinding[]>>({});
  const [bindingsLoading, setBindingsLoading] = useState<Record<number, boolean>>({});

  // Binding create/edit dialog
  const [bindingDialogOpen, setBindingDialogOpen] = useState(false);
  const [bindingDoctor, setBindingDoctor] = useState<Doctor | null>(null);
  const [editingBinding, setEditingBinding] = useState<DoctorTemplateBinding | null>(null);
  const [bindingForm, setBindingForm] = useState({
    cpt_code: '',
    modality: '',
    body_region: '',
    custom_prompt_override: '',
  });
  const [bindingError, setBindingError] = useState('');
  const [bindingSaving, setBindingSaving] = useState(false);

  const showSnack = (message: string, severity: 'success' | 'error') =>
    setSnackbar({ open: true, message, severity });

  const fetchDoctors = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await doctorService.searchDoctors({ include_inactive: true, limit: 100 });
      setDoctors(resp.items);
      setDoctorsTotal(resp.total);
      setError('');
    } catch {
      setError('Failed to load doctors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchDoctors();
  }, [isAdmin, fetchDoctors]);

  // ── Doctor CRUD ────────────────────────────────────────────────────

  const openCreateDoctor = () => {
    setEditingDoctor(null);
    setDoctorForm({ name: '', npi: '', specialty: '' });
    setDoctorError('');
    setDoctorDialogOpen(true);
  };

  const openEditDoctor = (doc: Doctor) => {
    setEditingDoctor(doc);
    setDoctorForm({
      name: doc.name,
      npi: doc.npi || '',
      specialty: doc.specialty || '',
    });
    setDoctorError('');
    setDoctorDialogOpen(true);
  };

  const handleSaveDoctor = async () => {
    if (!doctorForm.name.trim()) {
      setDoctorError('Name is required');
      return;
    }
    setDoctorSaving(true);
    setDoctorError('');
    try {
      const payload = {
        name: doctorForm.name.trim(),
        npi: doctorForm.npi.trim() || undefined,
        specialty: doctorForm.specialty.trim() || undefined,
      };
      if (editingDoctor) {
        await doctorService.updateDoctor(editingDoctor.id, payload);
        showSnack('Doctor updated', 'success');
      } else {
        await doctorService.createDoctor(payload);
        showSnack('Doctor created', 'success');
      }
      setDoctorDialogOpen(false);
      fetchDoctors();
    } catch (err: unknown) {
      const axErr = err as { response?: { data?: { detail?: string } } };
      setDoctorError(axErr.response?.data?.detail || 'Failed to save doctor');
    } finally {
      setDoctorSaving(false);
    }
  };

  const handleDeleteDoctor = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await doctorService.deactivateDoctor(deleteTarget.id);
      showSnack('Doctor deactivated', 'success');
      setDeleteTarget(null);
      fetchDoctors();
    } catch {
      showSnack('Failed to deactivate doctor', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Binding CRUD ───────────────────────────────────────────────────

  const fetchBindings = async (doctorId: number) => {
    setBindingsLoading((prev) => ({ ...prev, [doctorId]: true }));
    try {
      const resp = await doctorService.listBindings(doctorId);
      setBindingsByDoctor((prev) => ({ ...prev, [doctorId]: resp.items }));
    } catch {
      showSnack('Failed to load bindings', 'error');
    } finally {
      setBindingsLoading((prev) => ({ ...prev, [doctorId]: false }));
    }
  };

  const openCreateBinding = (doc: Doctor) => {
    setBindingDoctor(doc);
    setEditingBinding(null);
    setBindingForm({ cpt_code: '', modality: '', body_region: '', custom_prompt_override: '' });
    setBindingError('');
    setBindingDialogOpen(true);
  };

  const openEditBinding = (doc: Doctor, binding: DoctorTemplateBinding) => {
    setBindingDoctor(doc);
    setEditingBinding(binding);
    setBindingForm({
      cpt_code: binding.cpt_code || '',
      modality: binding.modality || '',
      body_region: binding.body_region || '',
      custom_prompt_override: binding.custom_prompt_override || '',
    });
    setBindingError('');
    setBindingDialogOpen(true);
  };

  const handleSaveBinding = async () => {
    if (!bindingDoctor) return;
    if (!bindingForm.custom_prompt_override.trim()) {
      setBindingError('Custom prompt override is required');
      return;
    }
    setBindingSaving(true);
    setBindingError('');
    try {
      const payload = {
        cpt_code: bindingForm.cpt_code.trim() || undefined,
        modality: bindingForm.modality.trim() || undefined,
        body_region: bindingForm.body_region.trim() || undefined,
        custom_prompt_override: bindingForm.custom_prompt_override.trim(),
      };
      if (editingBinding) {
        await doctorService.updateBinding(bindingDoctor.id, editingBinding.id, {
          custom_prompt_override: payload.custom_prompt_override,
          cpt_code: payload.cpt_code,
          modality: payload.modality,
          body_region: payload.body_region,
        });
        showSnack('Binding updated', 'success');
      } else {
        await doctorService.createBinding(bindingDoctor.id, payload);
        showSnack('Binding created', 'success');
      }
      setBindingDialogOpen(false);
      fetchBindings(bindingDoctor.id);
    } catch (err: unknown) {
      const axErr = err as { response?: { data?: { detail?: string } } };
      setBindingError(axErr.response?.data?.detail || 'Failed to save binding');
    } finally {
      setBindingSaving(false);
    }
  };

  const handleDeleteBinding = async (doc: Doctor, binding: DoctorTemplateBinding) => {
    try {
      await doctorService.deactivateBinding(doc.id, binding.id);
      showSnack('Binding deactivated', 'success');
      fetchBindings(doc.id);
    } catch {
      showSnack('Failed to deactivate binding', 'error');
    }
  };

  const handleApproveBinding = async (doc: Doctor, binding: DoctorTemplateBinding) => {
    try {
      await doctorService.approveBinding(doc.id, binding.id);
      showSnack('Binding approved', 'success');
      fetchBindings(doc.id);
    } catch {
      showSnack('Failed to approve binding', 'error');
    }
  };

  const handleBulkImport = async () => {
    if (!bulkCsvText.trim()) return;
    setBulkImportLoading(true);
    setBulkImportResult(null);
    try {
      const result = await doctorService.bulkImportDoctors(bulkCsvText);
      setBulkImportResult(result);
      if (result.created > 0) {
        showSnack(`Imported ${result.created} doctor(s)`, 'success');
        fetchDoctors();
      }
    } catch (err: unknown) {
      const axErr = err as { response?: { data?: { detail?: string } } };
      showSnack(axErr.response?.data?.detail || 'Bulk import failed', 'error');
    } finally {
      setBulkImportLoading(false);
    }
  };

  // ── Access guard ───────────────────────────────────────────────────
  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Access denied. Only administrators can access this page.</Alert>
      </Box>
    );
  }

  const bindingSourceLabel = (binding: DoctorTemplateBinding) => {
    if (binding.cpt_code) return `CPT: ${binding.cpt_code}`;
    if (binding.modality && binding.body_region) return `${binding.modality} / ${binding.body_region}`;
    if (binding.modality) return binding.modality;
    return 'Doctor-wide';
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PageHeader
        title="Doctor Template Management"
        subtitle="Manage doctors and their custom template bindings"
        onBack={() => (canGoBackInApp ? navigate(-1) : navigate('/'))}
        backLabel="Back"
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => { setBulkImportOpen(true); setBulkCsvText(''); setBulkImportResult(null); }}
            >
              Bulk Import
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDoctor}
            >
              Add Doctor
            </Button>
          </Stack>
        }
      />

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : doctors.length === 0 ? (
          <Alert severity="info">No doctors found. Click "Add Doctor" to create one.</Alert>
        ) : (
          <Stack spacing={1}>
            {doctorsTotal > doctors.length && (
              <Alert severity="warning">
                Showing {doctors.length} of {doctorsTotal} doctors. Use search to find specific doctors.
              </Alert>
            )}
            {doctors.map((doc) => (
              <Accordion
                key={doc.id}
                onChange={(_, expanded) => {
                  if (expanded && !bindingsByDoctor[doc.id]) fetchBindings(doc.id);
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                    <MedicalServicesIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle2">{doc.name}</Typography>
                    {doc.specialty && (
                      <Chip label={doc.specialty} size="small" variant="outlined" sx={{ height: 20 }} />
                    )}
                    {doc.npi && (
                      <Typography variant="caption" color="text.secondary">
                        NPI: {doc.npi}
                      </Typography>
                    )}
                    {!doc.is_active && (
                      <Chip label="inactive" size="small" color="error" sx={{ height: 20 }} />
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title="Edit doctor">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDoctor(doc);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deactivate doctor">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(doc);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" component="h6" color="text.secondary">
                      Template Bindings
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => openCreateBinding(doc)}
                    >
                      Add Binding
                    </Button>
                  </Box>
                  {bindingsLoading[doc.id] ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : bindingsByDoctor[doc.id]?.length ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Scope</TableCell>
                            <TableCell>Custom Prompt</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {bindingsByDoctor[doc.id].map((binding) => (
                            <TableRow key={binding.id}>
                              <TableCell>
                                <Chip
                                  label={bindingSourceLabel(binding)}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    maxWidth: 300,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {binding.custom_prompt_override || '(none)'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Chip
                                    label={binding.status}
                                    size="small"
                                    color={
                                      binding.status === 'approved'
                                        ? 'success'
                                        : binding.status === 'pending'
                                        ? 'warning'
                                        : 'default'
                                    }
                                    sx={{ height: 20 }}
                                  />
                                  {binding.approved_by && (
                                    <Tooltip title={`Approved by user #${binding.approved_by}`}>
                                      <CheckCircleIcon fontSize="small" color="success" />
                                    </Tooltip>
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Edit binding">
                                  <IconButton
                                    size="small"
                                    onClick={() => openEditBinding(doc, binding)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {binding.status === 'pending' && (
                                  <Tooltip title="Approve binding">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleApproveBinding(doc, binding)}
                                      color="success"
                                    >
                                      <CheckCircleIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Deactivate binding">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteBinding(doc, binding)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No bindings configured for this doctor.
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}
      </Box>

      {/* ── Doctor Create/Edit Dialog ────────────────────────────────── */}
      <Dialog open={doctorDialogOpen} onClose={() => setDoctorDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingDoctor ? 'Edit Doctor' : 'Add Doctor'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {doctorError && <Alert severity="error">{doctorError}</Alert>}
            <TextField
              label="Name"
              value={doctorForm.name}
              onChange={(e) => setDoctorForm((p) => ({ ...p, name: e.target.value }))}
              fullWidth
              required
              size="small"
              autoFocus
            />
            <TextField
              label="NPI (optional)"
              value={doctorForm.npi}
              onChange={(e) => setDoctorForm((p) => ({ ...p, npi: e.target.value }))}
              fullWidth
              size="small"
              placeholder="e.g. 1234567890"
            />
            <TextField
              label="Specialty (optional)"
              value={doctorForm.specialty}
              onChange={(e) => setDoctorForm((p) => ({ ...p, specialty: e.target.value }))}
              fullWidth
              size="small"
              placeholder="e.g. Neuroradiology"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDoctorDialogOpen(false)} disabled={doctorSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveDoctor}
            disabled={doctorSaving || !doctorForm.name.trim()}
            startIcon={doctorSaving ? <CircularProgress size={16} /> : undefined}
          >
            {editingDoctor ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Binding Create/Edit Dialog ───────────────────────────────── */}
      <Dialog open={bindingDialogOpen} onClose={() => setBindingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBinding ? 'Edit Template Binding' : 'Add Template Binding'}
          {bindingDoctor && ` — ${bindingDoctor.name}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {bindingError && <Alert severity="error">{bindingError}</Alert>}
            <Stack direction="row" spacing={2}>
              <TextField
                label="CPT Code (optional)"
                value={bindingForm.cpt_code}
                onChange={(e) => setBindingForm((p) => ({ ...p, cpt_code: e.target.value }))}
                size="small"
                placeholder="e.g. 70553"
                sx={{ flex: 1 }}
              />
              <TextField
                label="Modality (optional)"
                value={bindingForm.modality}
                onChange={(e) => setBindingForm((p) => ({ ...p, modality: e.target.value }))}
                size="small"
                placeholder="e.g. MRI"
                sx={{ flex: 1 }}
              />
              <TextField
                label="Body Region (optional)"
                value={bindingForm.body_region}
                onChange={(e) => setBindingForm((p) => ({ ...p, body_region: e.target.value }))}
                size="small"
                placeholder="e.g. Brain"
                sx={{ flex: 1 }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Leave CPT, modality, and body region blank for a doctor-wide binding.
              The resolution hierarchy is: CPT → modality+region → modality → doctor-wide.
            </Typography>
            <Box sx={{ height: 300 }}>
              <RichReportEditor
                value={bindingForm.custom_prompt_override}
                onChange={(text) => setBindingForm((p) => ({ ...p, custom_prompt_override: text }))}
                editing={!bindingSaving}
                preserveFormatting
                placeholder="Enter the custom template prompt…"
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBindingDialogOpen(false)} disabled={bindingSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveBinding}
            disabled={bindingSaving || !bindingForm.custom_prompt_override.trim()}
            startIcon={bindingSaving ? <CircularProgress size={16} /> : undefined}
          >
            {editingBinding ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk Import Dialog ────────────────────────────────────── */}
      <Dialog open={bulkImportOpen} onClose={() => setBulkImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Import Doctors</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Paste CSV data with columns: <strong>name</strong>, <strong>npi</strong> (optional), <strong>specialty</strong> (optional).
              The first row must be a header row.
            </Typography>
            <TextField
              label="CSV Data"
              value={bulkCsvText}
              onChange={(e) => setBulkCsvText(e.target.value)}
              multiline
              rows={8}
              fullWidth
              size="small"
              placeholder={'name,npi,specialty\nDr. John Smith,1234567890,Radiology\nDr. Jane Doe,,Orthopedics'}
              sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
            />
            {bulkImportResult && (
              <Alert severity={bulkImportResult.errors.length > 0 ? 'warning' : 'success'}>
                <Typography variant="body2">
                  Created: {bulkImportResult.created} | Skipped: {bulkImportResult.skipped}
                  {bulkImportResult.errors.length > 0 && ` | Errors: ${bulkImportResult.errors.length}`}
                </Typography>
                {bulkImportResult.errors.length > 0 && (
                  <Box sx={{ mt: 1, maxHeight: 120, overflow: 'auto' }}>
                    {bulkImportResult.errors.map((e, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block', color: 'error.main' }}>
                        {e}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkImportOpen(false)} disabled={bulkImportLoading}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkImport}
            disabled={bulkImportLoading || !bulkCsvText.trim()}
            startIcon={bulkImportLoading ? <CircularProgress size={16} /> : <UploadIcon />}
          >
            {bulkImportLoading ? 'Importing…' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Doctor Confirm ────────────────────────────────────── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Deactivate Doctor</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to deactivate{' '}
            <strong>{deleteTarget?.name}</strong>? This will deactivate the doctor and all their template bindings. The doctor can be reactivated later, but bindings will need to be re-created.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteDoctor}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : undefined}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ─────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DoctorManagementPage;
